import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Globe, Star, CheckCircle, MapPin, Calendar, Clock, Mail, ExternalLink, Menu, X } from 'lucide-react';

const Pricing = () => {
  const [language, setLanguage] = useState<'pl' | 'en'>('pl');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const content = {
    pl: {
      title: 'Dołącz do kursu: Rezerwacja i Inwestycja',
      subtitle: '#EducationExcellence #QualityTourism #TimeLeverage #ValuesDrivenEducation #TrustInTeaching #SustainablePerformance',
      pricing: {
        title: 'Cennik',
        subtitle: 'Gwarancja jakości: grupy maksymalnie 10 osób!',
        additionalInfo: 'Ważna informacja: Kursy oparte są na autorskich materiałach ekspertów.',
        single: {
          title: '1 Kurs (2,5 dnia)',
          price: '600 GBP',
          priceSecondary: '2900 PLN',
          includes: [
            '15 godzin sesji powtórkowych',
            'Autorskie materiały ekspertów',
            'Sesje CAS & Wellbeing',
            'Profesjonalna opieka'
          ]
        },
        double: {
          title: '2 Kursy (5 dni) - Rekomendowany',
          price: '1200 GBP',
          priceSecondary: '5800 PLN',
          includes: [
            '30 godzin sesji powtórkowych',
            'Autorskie materiały ekspertów',
            'Sesje CAS & Wellbeing',
            'Wspólne śniadania',
            'Profesjonalna opieka'
          ]
        },
        individual: {
          title: 'Cennik Sesji Indywidualnych',
          sessions: [
            {
              name: 'Konsultacje Q&A (17:00-19:00)',
              price: '120 GBP / 590 PLN'
            },
            {
              name: '45 min sesji 1:1 (EE, TOK, IAs, CAS)',
              price: '100 GBP / 490 PLN'
            }
          ]
        }
      },
      registration: {
        title: 'Zapisy i Rejestracja',
        subtitle: 'Proces zapisu krok po kroku',
        steps: [
          {
            title: 'Formularz zgłoszeniowy',
            description: 'Prosimy o wypełnienie formularza online dostępnego pod adresem:'
          },
          {
            title: 'Gwarancja miejsca',
            description: 'Podstawą wpisania na listę uczestników jest dokonanie pełnej wpłaty za wybrany kurs (rekomendujemy pakiet 2 kursów dla pełnej efektywności powtórek) na konto:'
          }
        ],
        bankDetails: {
          title: 'Dane do przelewu',
          account: 'PL 88 1140 2004 0000 3002 7993 6123',
          name: 'Fundacja WorldLINK Foundation',
          transferTitle: 'Tytuł przelewu: Działanie statutowe: Revision Course (s) February 2026 – [Imię i Nazwisko Ucznia] – [Wybrane Kursy np. Biology HL, Mathematics SL]'
        }
      },
      important: {
        title: 'Ważne informacje',
        items: [
          {
            title: 'Limit miejsc',
            description: 'W trosce o najwyższą jakość i komfort pracy, każda grupa liczy maksymalnie 10 osób.'
          },
          {
            title: 'Termin zapisów',
            description: 'Rekrutacja trwa do wyczerpania miejsc. O przyjęciu decyduje kolejność pełnych zgłoszeń (formularz + wpłata).'
          },
          {
            title: 'Sesje indywidualne',
            description: 'Zapisy na konsultacje 1:1 z wybranymi ekspertami odbywają się poprzez ten sam formularz. Liczba godzin konsultacyjnych jest ściśle ograniczona.'
          }
        ]
      },
      form: {
        title: 'ZAPISZ SIĘ',
        description: 'Wypełnij formularz rejestracyjny, aby zarezerwować swoje miejsce na kursie IB DP Accelerator.',
        openInNewTab: 'Otwórz w nowej karcie'
      }
    },
    en: {
      title: 'Join the DP Accelerator: Enrollment & Investment',
      subtitle: '#EducationExcellence #QualityTourism #TimeLeverage #ValuesDrivenEducation #TrustInTeaching #SustainablePerformance',
      pricing: {
        title: 'Pricing',
        subtitle: 'Quality guarantee: groups of maximum 10 people!',
        additionalInfo: 'Important note: Courses are based on proprietary materials from our experts.',
        single: {
          title: '1 Course (2.5 days)',
          price: '600 GBP',
          priceSecondary: '2900 PLN',
          includes: [
            '15 hours of review sessions',
            'Expert original materials',
            'CAS & Wellbeing sessions',
            'Professional supervision'
          ]
        },
        double: {
          title: '2 Courses (5 days) - Recommended',
          price: '1200 GBP',
          priceSecondary: '5800 PLN',
          includes: [
            '30 hours of review sessions',
            'Expert original materials',
            'CAS & Wellbeing sessions',
            'Shared breakfasts',
            'Professional supervision'
          ]
        },
        individual: {
          title: 'Individual Session Pricing',
          sessions: [
            {
              name: 'Q&A Consultations (17:00-19:00)',
              price: '120 GBP / 590 PLN'
            },
            {
              name: '45 min 1:1 session (EE, TOK, IAs, CAS)',
              price: '100 GBP / 490 PLN'
            }
          ]
        }
      },
      registration: {
        title: 'Registration Details',
        subtitle: 'Step-by-step enrollment process',
        steps: [
          {
            title: 'Application form',
            description: 'Please fill out the online form available at:'
          },
          {
            title: 'Place guarantee',
            description: 'The basis for enrollment is full payment for the selected course (we recommend the 2-course package for full review efficiency) to the account:'
          }
        ],
        bankDetails: {
          title: 'Bank transfer details',
          account: 'PL 88 1140 2004 0000 3002 7993 6123',
          name: 'WorldLINK Foundation',
          transferTitle: 'Transfer title: Statutory activity: Revision Course (s) February 2026 – [Student Name] – [Selected Courses e.g. Biology HL, Mathematics SL]'
        }
      },
      important: {
        title: 'Important Information',
        items: [
          {
            title: 'Place limit',
            description: 'For the highest quality and work comfort, each group has a maximum of 10 people.'
          },
          {
            title: 'Registration deadline',
            description: 'Recruitment continues until places are filled. Acceptance is determined by the order of complete applications (form + payment).'
          },
          {
            title: 'Individual sessions',
            description: 'Registration for 1:1 consultations with selected experts is done through the same form. The number of consultation hours is strictly limited.'
          }
        ]
      },
      form: {
        title: 'REGISTER',
        description: 'Fill out the registration form to reserve your place in the IB DP Accelerator course.',
        openInNewTab: 'Open in new tab'
      }
    }
  };

  const t = content[language];

  return (
    <div className="min-h-screen bg-background">
      {/* Navigation */}
      <nav className="border-b bg-white/95 backdrop-blur supports-[backdrop-filter]:bg-white/60 sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4 flex justify-between items-center">
          <Link to="/" className="flex items-center space-x-2">
            <div className="w-8 h-8 bg-gradient-to-r from-blue-600 to-yellow-500 rounded-full flex items-center justify-center">
              <span className="text-white font-bold text-sm">IB</span>
            </div>
            <span className="font-bold text-xl">DP Accelerator</span>
          </Link>
          
          <div className="hidden md:flex space-x-6">
            <Link to="/" className="hover:text-blue-600 transition-colors">{language === 'pl' ? 'Strona główna' : 'Home'}</Link>
            <Link to="/experts" className="hover:text-blue-600 transition-colors">{language === 'pl' ? 'Eksperci' : 'Experts'}</Link>
            <Link to="/schedule" className="hover:text-blue-600 transition-colors">{language === 'pl' ? 'Harmonogram' : 'Schedule'}</Link>
            <Link to="/course-topics" className="hover:text-blue-600 transition-colors">{language === 'pl' ? 'Tematy' : 'Topics'}</Link>
            <Link to="/pricing" className="text-blue-600 font-semibold">{language === 'pl' ? 'Dołącz do kursu' : 'Join the DP Accelerator'}</Link>
            <Link to="/code-of-conduct" className="hover:text-blue-600 transition-colors">{language === 'pl' ? 'Regulamin' : 'Code of Conduct'}</Link>
            <Link to="/contact" className="hover:text-blue-600 transition-colors">{language === 'pl' ? 'Kontakt' : 'Contact'}</Link>
          </div>
          
          <div className="flex items-center space-x-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setLanguage(language === 'pl' ? 'en' : 'pl')}
              className="flex items-center space-x-1"
            >
              <Globe className="w-4 h-4" />
              <span>{language === 'pl' ? 'EN' : 'PL'}</span>
            </Button>
            
            {/* Mobile menu button */}
            <Button
              variant="outline"
              size="sm"
              className="md:hidden"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? <X className="w-4 h-4" /> : <Menu className="w-4 h-4" />}
            </Button>
          </div>
        </div>
        
        {/* Mobile menu */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-white border-b shadow-lg">
            <div className="container mx-auto px-4 py-4 space-y-4">
              <Link 
                to="/" 
                className="block hover:text-blue-600 transition-colors"
                onClick={() => setMobileMenuOpen(false)}
              >
                {language === 'pl' ? 'Strona główna' : 'Home'}
              </Link>
              <Link 
                to="/experts" 
                className="block hover:text-blue-600 transition-colors"
                onClick={() => setMobileMenuOpen(false)}
              >
                {language === 'pl' ? 'Eksperci' : 'Experts'}
              </Link>
              <Link 
                to="/schedule" 
                className="block hover:text-blue-600 transition-colors"
                onClick={() => setMobileMenuOpen(false)}
              >
                {language === 'pl' ? 'Harmonogram' : 'Schedule'}
              </Link>
              <Link 
                to="/course-topics" 
                className="block hover:text-blue-600 transition-colors"
                onClick={() => setMobileMenuOpen(false)}
              >
                {language === 'pl' ? 'Tematy' : 'Topics'}
              </Link>
              <Link 
                to="/pricing" 
                className="block text-blue-600 font-semibold"
                onClick={() => setMobileMenuOpen(false)}
              >
                {language === 'pl' ? 'Dołącz do kursu' : 'Join the DP Accelerator'}
              </Link>
              <Link 
                to="/code-of-conduct" 
                className="block hover:text-blue-600 transition-colors"
                onClick={() => setMobileMenuOpen(false)}
              >
                {language === 'pl' ? 'Regulamin' : 'Code of Conduct'}
              </Link>
              <Link 
                to="/contact" 
                className="block hover:text-blue-600 transition-colors"
                onClick={() => setMobileMenuOpen(false)}
              >
                {language === 'pl' ? 'Kontakt' : 'Contact'}
              </Link>
            </div>
          </div>
        )}
      </nav>

      {/* Header */}
      <section className="py-20 bg-gradient-to-br from-blue-600 via-blue-700 to-yellow-500 text-white">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-5xl md:text-6xl font-bold mb-6 bg-gradient-to-r from-white to-yellow-200 bg-clip-text text-transparent">
            {t.title}
          </h1>
          <p className="text-xl md:text-2xl text-blue-100">
            {t.subtitle}
          </p>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold mb-4 text-gray-900">{t.pricing.title}</h2>
              <p className="text-xl text-gray-600">{t.pricing.subtitle}</p>
              <p className="text-lg text-blue-700 font-semibold mt-4">{t.pricing.additionalInfo}</p>
            </div>
            
            <div className="grid md:grid-cols-2 gap-8 mb-12">
              {/* 1 Course */}
              <Card className="hover:shadow-lg transition-shadow">
                <CardHeader>
                  <CardTitle className="text-2xl">{t.pricing.single.title}</CardTitle>
                  <div className="text-3xl font-bold text-blue-600">
                    {t.pricing.single.price} / {t.pricing.single.priceSecondary}
                  </div>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2">
                    {t.pricing.single.includes.map((item, index) => (
                      <li key={index} className="flex items-center">
                        <Star className="w-4 h-4 text-yellow-500 mr-2" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
              
              {/* 2 Courses */}
              <Card className="hover:shadow-lg transition-shadow border-blue-200 bg-blue-50">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-2xl">{t.pricing.double.title}</CardTitle>
                    <Badge className="bg-blue-600">{language === 'pl' ? 'Popularne' : 'Popular'}</Badge>
                  </div>
                  <div className="text-3xl font-bold text-blue-600">
                    {t.pricing.double.price} / {t.pricing.double.priceSecondary}
                  </div>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2">
                    {t.pricing.double.includes.map((item, index) => (
                      <li key={index} className="flex items-center">
                        <Star className="w-4 h-4 text-yellow-500 mr-2" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            </div>
            
            {/* Individual Sessions */}
            <Card className="bg-gray-50 mb-12">
              <CardHeader>
                <CardTitle className="text-2xl text-center">{t.pricing.individual.title}</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-2 gap-6">
                  {t.pricing.individual.sessions.map((session, index) => (
                    <div key={index} className="bg-white p-4 rounded-lg shadow">
                      <div className="flex justify-between items-center">
                        <span className="font-medium">{session.name}</span>
                        <span className="font-bold text-blue-600">{session.price}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Registration Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold mb-4 text-gray-900">{t.registration.title}</h2>
              <p className="text-xl text-gray-600">{t.registration.subtitle}</p>
            </div>
            
            <div className="space-y-8 mb-12">
              {/* Step 1 */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center">
                    <div className="w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center mr-3 text-sm font-bold">1</div>
                    {t.registration.steps[0].title}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600 mb-4">{t.registration.steps[0].description}</p>
                  <div className="bg-blue-50 p-4 rounded-lg">
                    <a 
                      href="https://forms.gle/Ncctf4rbqzSmtpkc9" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-blue-600 font-semibold hover:text-blue-800 flex items-center"
                    >
                      https://forms.gle/Ncctf4rbqzSmtpkc9
                      <ExternalLink className="w-4 h-4 ml-2" />
                    </a>
                  </div>
                </CardContent>
              </Card>
              
              {/* Step 2 */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center">
                    <div className="w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center mr-3 text-sm font-bold">2</div>
                    {t.registration.steps[1].title}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600 mb-4">{t.registration.steps[1].description}</p>
                  <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                    <h4 className="font-semibold text-green-900 mb-2">{t.registration.bankDetails.title}</h4>
                    <div className="space-y-2 text-green-800">
                      <p><strong>{language === 'pl' ? 'Nr konta:' : 'Account number:'}</strong> {t.registration.bankDetails.account}</p>
                      <p><strong>{language === 'pl' ? 'Odbiorca:' : 'Recipient:'}</strong> {t.registration.bankDetails.name}</p>

                    </div>
                    <div className="mt-4 p-3 bg-green-100 rounded border-l-4 border-green-600">
                      <p className="text-green-900 text-sm">
                        <strong>{language === 'pl' ? 'Tytuł przelewu:' : 'Transfer title:'}</strong><br />
                        {t.registration.bankDetails.transferTitle}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Important Information */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-bold text-center mb-12 text-gray-900">{t.important.title}</h2>
            <div className="grid gap-6">
              {t.important.items.map((item, index) => (
                <Card key={index} className="border-l-4 border-amber-500">
                  <CardHeader>
                    <CardTitle className="text-xl text-amber-900">{item.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-gray-700">{item.description}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Registration Form */}
      <section className="py-20 bg-blue-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold mb-4 text-blue-900">{t.form.title}</h2>
              <p className="text-xl text-blue-800">{t.form.description}</p>
            </div>
            
            <Card className="bg-white">
              <CardContent className="p-8">
                <div className="text-center">
                  <a 
                    href="https://forms.gle/Ncctf4rbqzSmtpkc9" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="inline-flex items-center px-6 py-3 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    <ExternalLink className="w-5 h-5 mr-2" />
                    {t.form.openInNewTab}
                  </a>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="container mx-auto px-4">
          <div className="text-center">
            <Link to="/" className="flex items-center justify-center space-x-2 mb-4">
              <div className="w-8 h-8 bg-gradient-to-r from-blue-600 to-yellow-500 rounded-full flex items-center justify-center">
                <span className="text-white font-bold text-sm">IB</span>
              </div>
              <span className="font-bold text-xl">DP Accelerator</span>
            </Link>
            <p className="text-gray-400">
              {language === 'pl' 
                ? 'Intensywne kursy przygotowawcze do matury międzynarodowej IB.' 
                : 'Intensive preparation courses for the International Baccalaureate.'}
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Pricing;